// A searchable lookup on a list endpoint (revamp PR 6b): the Lumen AsyncSelect. It keeps AsyncSelect's request rules
// exactly (async-combobox-logic.ts, and the effects below, ported line by line from
// `src/components/async-select/AsyncSelect.tsx` + `searchable-select-input` + `ui/combobox`'s input mode):
// - lazy: the list loads on the first focus (or at once with `loadOnMount` / `useLocalQuery`, or for a value that has
//   no other way to get its label);
// - `GET {path}?offset=0&limit=10&{dynamicKeyParam}={text}[&{additionalParam}]`, the text not encoded; a focus with
//   at most one option loads the full list (`text` empty);
// - the typed text reaches the query 300 ms + 500 ms after the last keystroke (two debounces, as before);
// - a value's label comes from `GET {defaultValuePath}{value}` unless `initialSearchValue` gives it or the value
//   already reads `code - name`;
// - React Query keys `['async-select-options', path, text, additionalParam]` and
//   `['async-select-default', defaultValuePath, value]`, shared with AsyncSelect; 12 h cache (24 h for HS codes,
//   none for uoms, packages or `noCache`);
// - the inner field remounts when the value changes (pending debounces die with it), and an emptied field gets its
//   focus back.
// `onChange(value, option)`: a pick gives the option ({ label, value, origin: the response record, key }); emptying the
// text gives `('', null)` at once (and again after the debounce if the parent kept the value). The option is typed
// `AsyncComboboxOption | null`, so a handler can't destructure it: AsyncSelect's `onChange={(val, { origin }) => …}`
// threw on clear (it passed undefined, i1 bug 12.5); with AsyncCombobox that handler is a type error.
//
//   <Field id={id} label={t('…kantor')} required error={error}>
//     <AsyncCombobox
//       id={id}                                       // default: AsyncSelect's id, `async-select-{path}`
//       path="/api/v1/pabean-offices"
//       valueOnResponseKey="code"                     // default 'id'
//       optionsLabelKeys={['code', 'name']}           // label = the values joined with ' - '
//       defaultValuePath="/api/v1/pabean-offices?query="
//       value={form.kodeKantor}
//       onChange={(value, option) => set('kodeKantor', value)}
//       onBlur={() => touch('kodeKantor')}
//       placeholder={t('…kantor')}
//       loadingText={t('…loading')} emptyText={t('…notFound')} loadErrorText={…}
//       required invalid={!!error} aria-describedby={fieldDescribedBy(id, { error: !!error })}
//     />
//   </Field>
//
// Optional, as on AsyncSelect: `dynamicKeyParam` ('query'), `additionalParam`, `initialSearchValue`, `disabled`,
// `noCache`, `useDefault`, `useLocalQuery`, `loadOnMount`, `clearable` (+ `clearLabel`), `name`, `className`,
// `extraOptions` (static options listed first, as the upload kit's "OF - Ocean Freight"), `filterOptions` (with
// `useLocalQuery`, as the upload kit's `rankDocTypeOptions`), `renderOption` (below).
// `renderOption(origin)` draws a fetched row's content in the list (a badge beside the code, PR 7c's HS bulk edit);
// the label stays the string, so the search, the text shown after a pick and `onChange` are unchanged. Rows with no
// response record (`extraOptions`, the disabled and fallback rows) still show their label. Default: the label.
// Not carried over (no user): `data`, `size`, `style`.
import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown, Loader2, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/lumen/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/lumen/popover';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import axiosInstance from '@libs/axios';
import {
  type AsyncComboboxOption,
  asyncComboboxId,
  type ExtraOption,
  type OptionsFilter,
  findExtraOption,
  NO_EXTRA_OPTIONS,
  DEFAULT_LABEL_CACHE,
  defaultLabelEnabled,
  defaultLabelQueryKey,
  defaultLabelText,
  defaultLabelUrl,
  deriveOptions,
  listEnabled,
  optionsCacheTimes,
  optionsQueryKey,
  optionsUrl,
  queryForFetch,
} from './async-combobox-logic';

export type { AsyncComboboxOption } from './async-combobox-logic';

export interface AsyncComboboxProps {
  value: string;
  /** A pick: `(value, option)`. Emptying the text: `('', null)`. Handle the null: never destructure the option. */
  onChange: (value: string, option: AsyncComboboxOption | null) => void;
  path: string;
  optionsLabelKeys: string[];
  valueOnResponseKey?: string;
  dynamicKeyParam?: string;
  additionalParam?: string;
  defaultValuePath?: string;
  /** A label for the value that saves the default-label GET (and seeds the search text). */
  initialSearchValue?: string;
  placeholder?: string;
  loadingText: string;
  emptyText: string;
  /** The load-error toast's text. */
  loadErrorText: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
  onBlur?: () => void;
  noCache?: boolean;
  useDefault?: boolean;
  useLocalQuery?: boolean;
  loadOnMount?: boolean;
  clearable?: boolean;
  clearLabel?: string;
  className?: string;
  /** Static options listed before the fetched ones, filtered by the search text; a value that names one shows its
   *  label without the default-label GET (the endpoint has no row for it). AsyncSelect's `extraOptions`. */
  extraOptions?: ReadonlyArray<ExtraOption>;
  /** No list GET while true: the options are `extraOptions` alone (AsyncSelect's `data`, which bypassed all fetching). */
  fetchDisabled?: boolean;
  /** With `useLocalQuery`: filters and ranks the whole list by the search text (AsyncSelect's `filterOptions`). Pass a
   *  stable function (module level), it is a memo dependency. */
  filterOptions?: OptionsFilter;
  /** A fetched row's content in the list, from its response record (the label still searches and shows after a
   *  pick). Rows without a record show their label. */
  renderOption?: (origin: unknown) => ReactNode;
}

export function AsyncCombobox({
  value = '',
  onChange,
  path,
  optionsLabelKeys,
  valueOnResponseKey = 'id',
  dynamicKeyParam = 'query',
  additionalParam = '',
  defaultValuePath,
  initialSearchValue = '',
  placeholder,
  loadingText,
  emptyText,
  loadErrorText,
  id,
  name,
  disabled = false,
  required = false,
  invalid,
  'aria-describedby': describedBy,
  onBlur,
  noCache = false,
  useDefault = true,
  useLocalQuery = false,
  loadOnMount = false,
  clearable = false,
  clearLabel,
  className,
  extraOptions = NO_EXTRA_OPTIONS,
  fetchDisabled = false,
  filterOptions,
  renderOption,
}: AsyncComboboxProps) {
  const { t } = useLanguage();
  const [isComponentRendered, setIsComponentRendered] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchValue);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchValue);
  // When true, the list fetches with an empty text: the full dropdown list.
  const [showFullList, setShowFullList] = useState(false);
  // Latches once the field is focused, so the list loads on demand (loadOnMount loads it at once).
  const [hasOpened, setHasOpened] = useState(loadOnMount);
  const skipNextSearchRef = useRef(false);
  // Set when the person empties a focused field: the inner field remounts on the value change and needs focus back.
  const refocusRef = useRef(false);
  const stableId = id || asyncComboboxId(path);

  useEffect(() => {
    setIsComponentRendered(true);
  }, []);

  // Sync the shown text when initialSearchValue changes from outside.
  // Dependencies: AsyncSelect's effect, on initialSearchValue only
  useEffect(() => {
    if (initialSearchValue && initialSearchValue !== searchQuery) {
      setSearchQuery(initialSearchValue);
      setDebouncedSearchQuery(initialSearchValue);
    }
  }, [initialSearchValue]);

  // Disabled: show the value's label.
  // Dependencies: AsyncSelect's effect, without searchQuery
  useEffect(() => {
    if (!disabled) return;
    const labelToShow = initialSearchValue || value;
    if (labelToShow && searchQuery !== labelToShow) {
      setSearchQuery(labelToShow);
      setDebouncedSearchQuery(labelToShow);
    }
  }, [disabled, value, initialSearchValue]);

  // The second debounce (500 ms); skips one round after a pick so the picked label is not searched.
  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchText = queryForFetch({ showFullList, useLocalQuery, debouncedSearch: debouncedSearchQuery });
  const cache = optionsCacheTimes(path, noCache);
  const {
    data: optionsData,
    isFetching: isLoadingOptions,
    error: optionsError,
  } = useQuery({
    queryKey: optionsQueryKey(path, fetchText, additionalParam),
    queryFn: async () => {
      const response = await axiosInstance.get(
        optionsUrl({ path, query: fetchText, dynamicKeyParam, additionalParam, useDefault })
      );
      return response.data;
    },
    staleTime: cache.staleTime,
    gcTime: cache.gcTime,
    enabled: listEnabled({
      rendered: isComponentRendered,
      disabled,
      fetchDisabled,
      useLocalQuery,
      hasOpened,
      value,
      defaultValuePath,
      initialSearchValue,
    }),
    throwOnError: false,
  });

  useEffect(() => {
    if (optionsError) toast.error(t('lumen.common.error'), { description: loadErrorText });
  }, [optionsError, loadErrorText, t]);

  const { data: defaultValueData, isFetching: isLoadingDefault } = useQuery({
    queryKey: defaultLabelQueryKey(defaultValuePath, value),
    queryFn: async () => {
      const response = await axiosInstance.get(defaultLabelUrl(defaultValuePath ?? '', value));
      return response.data;
    },
    staleTime: DEFAULT_LABEL_CACHE.staleTime,
    gcTime: DEFAULT_LABEL_CACHE.gcTime,
    enabled: defaultLabelEnabled({
      rendered: isComponentRendered,
      disabled,
      value,
      defaultValuePath,
      initialSearchValue,
      extraOptions,
    }),
    throwOnError: false,
  });

  // The value's fetched label becomes the shown text (not the search: the skip flag stops the debounce).
  // Dependencies: AsyncSelect's effect, on defaultValueData only
  useEffect(() => {
    if (!defaultValueData || disabled) return;
    const applyDisplay = (text: string) => {
      setSearchQuery((prev) => {
        // Arm the skip only when the text changes, or the flag would swallow the next real keystroke.
        if (prev === text) return prev;
        skipNextSearchRef.current = true;
        return text;
      });
    };
    if (!defaultValueData.data) {
      applyDisplay(value);
      return;
    }
    const labelOpt = defaultLabelText(defaultValueData, optionsLabelKeys);
    if (labelOpt) applyDisplay(labelOpt);
  }, [defaultValueData]);

  // A custom option's label comes from the option, not from a fetch (the default-label GET is skipped for it).
  // Dependencies: AsyncSelect's effect, on the selected custom option only
  const selectedExtra = useMemo(() => findExtraOption(extraOptions, value), [extraOptions, value]);
  useEffect(() => {
    if (!selectedExtra || disabled) return;
    setSearchQuery((prev) => {
      // Arm the skip only when the text changes, or the flag would swallow the next real keystroke.
      if (prev === selectedExtra.label) return prev;
      skipNextSearchRef.current = true;
      return selectedExtra.label;
    });
  }, [selectedExtra, disabled]);

  const options = useMemo(
    () =>
      deriveOptions({
        disabled,
        value,
        initialSearchValue,
        useLocalQuery,
        showFullList,
        searchQuery,
        debouncedSearch: debouncedSearchQuery,
        optionsData,
        defaultData: defaultValueData,
        optionsLabelKeys,
        valueOnResponseKey,
        stableId,
        extraOptions,
        filterOptions,
      }),
    [
      disabled,
      value,
      initialSearchValue,
      useLocalQuery,
      showFullList,
      searchQuery,
      debouncedSearchQuery,
      optionsData,
      defaultValueData,
      optionsLabelKeys,
      valueOnResponseKey,
      stableId,
      extraOptions,
      filterOptions,
    ]
  );

  const loading = isLoadingOptions || isLoadingDefault;

  useEffect(() => {
    if (!refocusRef.current) return;
    refocusRef.current = false;
    // Only a value that went empty: a later pick must not pull focus back.
    if (!value) document.getElementById(stableId)?.focus();
  }, [value, stableId]);

  const displayValue = searchQuery || initialSearchValue || '';

  const handleFocus = () => {
    if (disabled) return;
    setHasOpened(true);
    if (useLocalQuery) return;
    // A list with only the selected row would open nearly empty: load the full list.
    if (options.length <= 1 && !isLoadingOptions) setShowFullList(true);
  };

  /** A pick, or an emptied field ('' ), from the inner field. AsyncSelect's onChange wrapper. */
  const handleValue = (val: string) => {
    if (!val) {
      // Emptied, not picked: the inner field remounts on the value change and reads its text from here.
      setSearchQuery('');
      setDebouncedSearchQuery('');
      refocusRef.current = !!value && document.activeElement?.id === stableId;
      // A parent that ignores '' never changes the value; don't let the flag outlive this event.
      window.setTimeout(() => {
        refocusRef.current = false;
      }, 0);
    }
    const selectedOption = options.find((r) => String(r.value) === String(val));
    if (selectedOption) {
      skipNextSearchRef.current = true;
      setSearchQuery(String(selectedOption.label));
      setDebouncedSearchQuery('');
      setShowFullList(false);
    }
    onChange(val, selectedOption ?? null);
  };

  /** The typed text, 300 ms after the last keystroke. AsyncSelect's onSearchChange. */
  const handleSearch = (val: string) => {
    setSearchQuery(val);
    setShowFullList(false);
    if (!val) onChange('', null);
  };

  return (
    <AsyncComboboxInput
      key={`${stableId}-${value}`}
      id={stableId}
      name={name}
      options={options}
      value={value}
      displayValue={displayValue}
      loading={loading}
      placeholder={placeholder}
      loadingText={loadingText}
      emptyText={emptyText}
      disabled={disabled}
      required={required}
      invalid={invalid}
      describedBy={describedBy}
      clearable={clearable}
      clearLabel={clearLabel}
      className={className}
      onValue={handleValue}
      onSearch={handleSearch}
      onFocus={handleFocus}
      onBlur={onBlur}
      renderOption={renderOption}
    />
  );
}

interface AsyncComboboxInputProps {
  id: string;
  name?: string;
  options: AsyncComboboxOption[];
  value: string;
  displayValue: string;
  loading: boolean;
  placeholder?: string;
  loadingText: string;
  emptyText: string;
  disabled: boolean;
  required: boolean;
  invalid?: boolean;
  describedBy?: string;
  clearable: boolean;
  clearLabel?: string;
  className?: string;
  onValue: (value: string) => void;
  onSearch: (text: string) => void;
  onFocus: () => void;
  onBlur?: () => void;
  renderOption?: (origin: unknown) => ReactNode;
}

/**
 * The field itself, remounted on every value change (AsyncSelect keyed its SearchableSelect on the value). It holds
 * the shown text, the 300 ms debounce of the search (cancelled on unmount) and the open list.
 */
function AsyncComboboxInput({
  id,
  name,
  options,
  value,
  displayValue,
  loading,
  placeholder,
  loadingText,
  emptyText,
  disabled,
  required,
  invalid,
  describedBy,
  clearable,
  clearLabel,
  className,
  onValue,
  onSearch,
  onFocus,
  onBlur,
  renderOption,
}: AsyncComboboxInputProps) {
  const listId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef(0);
  const [search, setSearchText] = useState(displayValue);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setSearchText(displayValue);
  }, [displayValue]);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);
  // Dependencies: a new list starts on its first row (cmdk did the same)
  useEffect(() => {
    setActive(0);
  }, [options]);

  /** SearchableSelect's onSearchChange: show the text, search it in 300 ms, and an empty text clears the value. */
  const setSearch = (next: string) => {
    setSearchText(next);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = 0;
      onSearch(next);
    }, 300);
    if (!next) onValue('');
  };

  const selected = options.find((opt) => String(opt.value) === String(value ?? ''));
  const selectedLabel = selected ? String(selected.label) : '';

  const show = (next: boolean) => {
    if (disabled) return;
    setOpen(next);
  };

  const selectValue = (nextValue: string) => {
    const next = clearable && String(nextValue) === String(value ?? '') ? null : nextValue;
    onValue(next ?? '');
    const opt = options.find((o) => String(o.value) === String(nextValue));
    if (opt) setSearch(String(opt.label));
    show(false);
  };

  const selectActiveOrFirst = () => {
    if (loading) return;
    const row = options[active] ?? options[0];
    if (row) selectValue(String(row.value));
  };

  return (
    <Popover open={open && !disabled} onOpenChange={show}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} data-slot="async-combobox" className={cn('relative w-full', className)}>
          <Input
            ref={inputRef}
            id={id}
            name={name}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open && !loading && options[active] ? `${listId}-${active}` : undefined}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-required={required || undefined}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              if (!open) show(true);
            }}
            onClick={() => show(true)}
            onFocus={(event) => {
              // The next keystroke replaces the label: emptying the box by hand would clear the value.
              event.currentTarget.select();
              onFocus();
              show(true);
            }}
            onBlur={() => {
              // After a click on a row has landed: put the selected label back over a half-typed search.
              window.setTimeout(() => {
                if (value && selectedLabel && search !== selectedLabel) setSearch(selectedLabel);
                onBlur?.();
              }, 120);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                show(false);
                return;
              }
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                if (!open) show(true);
                const n = options.length;
                if (n) setActive((i) => (event.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
                return;
              }
              if (!open || loading) return;
              if (event.key === 'Enter') {
                event.preventDefault();
                selectActiveOrFirst();
              }
            }}
            className="pr-9"
          />
          {clearable && search && !disabled ? (
            <button
              type="button"
              aria-label={clearLabel}
              tabIndex={-1}
              className="absolute top-1/2 right-2 grid size-5 -translate-y-1/2 place-items-center rounded-sm text-fg-muted hover:text-fg"
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setSearch('');
                inputRef.current?.focus();
                if (!open) show(true);
              }}
            >
              <X aria-hidden className="size-4" />
            </button>
          ) : (
            <ChevronsUpDown
              aria-hidden
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-fg-muted"
            />
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) p-1"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onFocusOutside={(event) => event.preventDefault()}
        onMouseDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        {loading ? (
          <div role="status" className="flex items-center justify-center gap-2 py-6 text-fg-muted text-sm">
            <Loader2 aria-hidden className="size-4 animate-spin motion-reduce:animate-none" />
            {loadingText}
          </div>
        ) : options.length === 0 ? (
          <p className="py-6 text-center text-fg-muted text-sm">{emptyText}</p>
        ) : (
          <div id={listId} role="listbox" className="grid max-h-60 overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = String(option.value) === String(value ?? '');
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: the input owns the keyboard (aria-activedescendant)
                <div
                  key={option.key || `${option.value}-${option.label}`}
                  id={`${listId}-${index}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={isSelected}
                  data-active={index === active || undefined}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => selectValue(String(option.value))}
                  className="flex cursor-pointer items-center gap-2 rounded-control px-2.5 py-1.5 text-fg text-sm data-active:bg-tint"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {option.origin && renderOption ? renderOption(option.origin) : option.label}
                  </span>
                  {isSelected && <span aria-hidden className="size-1.5 rounded-full bg-label" />}
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
